import ServicePrice from "../components/ServicePrice";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Clock,
  User,
  Phone,
  Mail,
  Edit2,
  Copy,
  Share2,
  MessageCircle,
  Star,
} from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useCustomer } from "../contexts/CustomerContext";
import { toast } from "sonner";

export const BookingDetailPage = () => {
  const navigate = useNavigate();
  const { booking_id } = useParams();
  const { customer, token, loading: authLoading, requestLogin } = useCustomer();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roPhotoOpen, setRoPhotoOpen] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!customer || !token) {
      sessionStorage.setItem("repairro_booking_return", `/dashboard/bookings/${encodeURIComponent(booking_id)}`);
      requestLogin();
      return;
    }

    fetchBookingDetail();
  }, [booking_id, customer, token, navigate, authLoading, requestLogin]);

  const fetchBookingDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/customer/bookings/${booking_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setBooking(response.data);
    } catch (err) {
      console.error("Failed to fetch booking detail:", err);
      if (err.response?.status === 403) {
        toast.error("You don't have access to this booking");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load booking details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBookingId = () => {
    navigator.clipboard.writeText(booking.booking_id);
    toast.success("Booking ID copied to clipboard");
  };

  const handleCancelBooking = async () => {
    if (!booking) return;

    const confirmed = window.confirm("Are you sure you want to cancel this booking?");
    if (!confirmed) return;

    try {
      const response = await axios.post(
        `${API}/customer/bookings/${booking_id}/cancel`,
        { reason: "Customer requested cancellation" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBooking(response.data);
      toast.success("Booking cancelled successfully");
    } catch (err) {
      console.error("Failed to cancel booking:", err);
      toast.error(err.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const normalizeStatus = (status) => {
    const key = String(status || "").toLowerCase().trim().replace(/\s+/g, "_");
    const aliases = {
      en_route: "on_the_way",
      "en-route": "on_the_way",
      arrived: "on_the_way",
      on_the_way: "on_the_way",
      accepted: "accepted",
      assigned: "assigned",
      confirmed: "confirmed",
      work_started: "work_started",
      inspected: "inspected",
      completed: "completed",
      cancelled: "cancelled",
    };
    return aliases[key] || key;
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-blue-100 text-blue-700 border-blue-300",
      assigned: "bg-purple-100 text-purple-700 border-purple-300",
      accepted: "bg-purple-100 text-purple-700 border-purple-300",
      on_the_way: "bg-orange-100 text-orange-700 border-orange-300",
      work_started: "bg-yellow-100 text-yellow-700 border-yellow-300",
      inspected: "bg-cyan-100 text-cyan-700 border-cyan-300",
      completed: "bg-green-100 text-green-700 border-green-300",
      cancelled: "bg-red-100 text-red-700 border-red-300",
    };
    return colors[normalizeStatus(status)] || "bg-slate-100 text-slate-700";
  };

  const getStatusLabel = (status) => {
    const labels = {
      confirmed: "Confirmed",
      assigned: "Technician Assigned",
      accepted: "Accepted",
      on_the_way: "On The Way",
      work_started: "Work Started",
      inspected: "Inspected",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return labels[normalizeStatus(status)] || status;
  };

  if (!customer) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-8 h-8 border-4 border-[#0369A1] border-t-transparent rounded-full"></div>
          </div>
          <p className="text-slate-500">Loading booking details...</p>
        </div>
      </div>
    );
  }

  const roPhotoUrl = booking?.ro_photo_url || booking?.product_image_url;

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 text-lg">Booking not found</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 text-[#0369A1] hover:underline"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {roPhotoOpen && roPhotoUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
          onClick={() => setRoPhotoOpen(false)}
        >
          <div className="relative max-w-5xl w-full flex items-center justify-center">
            <img
              src={roPhotoUrl}
              alt="RO inspection zoom"
              className="max-h-[90vh] max-w-full rounded-xl border border-white/20 shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {booking.service_name}
              </h1>
              <p className="text-slate-500 mt-1">Booking #{booking.booking_id}</p>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(
                booking.status
              )}`}
            >
              {getStatusLabel(booking.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Details */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Service Details
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-600">Service</span>
                  <span className="font-semibold text-slate-900">
                    {booking.service_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Category</span>
                  <span className="font-semibold text-slate-900">
                    {booking.category}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Brand</span>
                  <span className="font-semibold text-slate-900">
                    {booking.brand}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Customer Type</span>
                  <span className="font-semibold text-slate-900">
                    {booking.customer_type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Service charge</span>
                  <span className="font-semibold text-slate-900">
                    <ServicePrice service={booking} />
                  </span>
                </div>
                {(booking.extra_amount || booking.final_price) && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Extra amount</span>
                      <span className="font-semibold text-slate-900">
                        ₹{booking.extra_amount ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-3 mt-2">
                      <span className="text-slate-600">Final amount</span>
                      <span className="font-semibold text-slate-900">
                        ₹{booking.final_price ?? booking.price ?? 0}
                      </span>
                    </div>
                  </>
                )}

                {booking.is_amc && (
                  <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <div className="font-semibold mb-2">AMC details</div>
                    <div className="space-y-1">
                      <div>Coverage status: {booking.amc_status || "pending"}</div>
                      <div>Start date: {booking.amc_start_date || "—"}</div>
                      <div>Expiry date: {booking.amc_end_date || "—"}</div>
                      <div>Renewals: {booking.renewal_count ?? 0}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(booking.ro_type || booking.service_needed || booking.parts_replaced?.length || booking.parts_to_change?.length || booking.inspection_note) && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Technician Inspection</h2>
                <div className="space-y-4">
                  {booking.ro_type && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">RO type</span>
                      <span className="font-semibold text-slate-900">{booking.ro_type}</span>
                    </div>
                  )}
                  {booking.service_needed && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Service needed</span>
                      <span className="font-semibold text-slate-900">{booking.service_needed}</span>
                    </div>
                  )}
                  {(booking.parts_replaced?.length || booking.parts_to_change?.length) && (
                    <div>
                      <div className="text-slate-600 mb-1">Parts changed</div>
                      <div className="font-semibold text-slate-900">
                        {(booking.parts_replaced || booking.parts_to_change || []).join(", ")}
                      </div>
                    </div>
                  )}
                  {booking.inspection_note && (
                    <div>
                      <div className="text-slate-600 mb-1">Technician note</div>
                      <div className="font-semibold text-slate-900">{booking.inspection_note}</div>
                    </div>
                  )}
                  {roPhotoUrl && (
                    <div>
                      <div className="text-slate-600 mb-2">RO photo</div>
                      <button
                        type="button"
                        onClick={() => setRoPhotoOpen(true)}
                        className="block w-full text-left cursor-zoom-in"
                      >
                        <img
                          src={roPhotoUrl}
                          alt="RO inspection"
                          className="w-full max-w-md rounded-lg border border-slate-200 object-contain max-h-72 bg-slate-50"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scheduling */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">
                Scheduled Details
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#0369A1]" />
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-semibold text-slate-900">{booking.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#0369A1]" />
                  <div>
                    <p className="text-sm text-slate-500">Time Slot</p>
                    <p className="font-semibold text-slate-900">{booking.slot}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#0369A1]" />
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-semibold text-slate-900">
                      {booking.address}, {booking.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Technician Info (if assigned) */}
            {booking.technician_name && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">
                  Technician
                </h2>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {booking.technician_name}
                    </p>
                    <p className="text-sm text-slate-500">Professional Technician</p>
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 bg-[#0369A1] hover:bg-[#075985] text-white rounded-lg font-semibold transition">
                    <MessageCircle className="w-4 h-4" /> Contact
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleCopyBookingId}
                  className="w-full flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 transition"
                >
                  <Copy className="w-4 h-4" /> Copy ID
                </button>
                <button className="w-full flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 transition">
                  <Share2 className="w-4 h-4" /> Share
                </button>
                {booking.status !== "cancelled" &&
                  booking.status !== "completed" && (
                    <button
                      onClick={handleCancelBooking}
                      className="w-full flex items-center gap-2 px-4 py-2.5 border border-red-300 rounded-lg text-red-700 font-semibold hover:bg-red-50 transition"
                    >
                      Cancel Booking
                    </button>
                  )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">Timeline</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-slate-500">Created</p>
                  <p className="font-semibold text-slate-900">{booking.created_at}</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Updated</p>
                  <p className="font-semibold text-slate-900">{booking.updated_at}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
